<script setup lang="ts">
import type { PropType } from 'vue'
import { computed } from 'vue'
import { cva } from 'class-variance-authority'
import { SwitchSelectorSize } from '../../../../interfaces/input'

const props = defineProps({
    size: {
        type: String as PropType<SwitchSelectorSize>,
        default: SwitchSelectorSize.LARGE
    },
    label: {
        type: String,
        default: ''
    },
    isSelected: {
        type: Boolean,
        default: false
    },
    isDisabled: {
        type: Boolean,
        default: false
    },
    error: {
        type: String,
        default: ''
    }
})

const value = defineModel<boolean>({
    required: true
})

const isError = computed(() => props.error !== '' && !props.isDisabled)

const containerStyles = computed(() =>
    cva('w-full flex flex-col gap-0.5 rounded', {
        variants: {
            isDefault: {
                true: 'md:hover:bg-badge-neutral'
            },
            isSelected: {
                true: 'border border-light-blue-primary bg-primary-light'
            },
            size: {
                [SwitchSelectorSize.LARGE]: 'p-2',
                [SwitchSelectorSize.MEDIUM]: 'px-2 py-1'
            },
            isDisabled: {
                true: 'cursor-not-allowed',
                false: 'cursor-pointer'
            }
        }
    })({
        isDefault: !props.isSelected && !props.isDisabled,
        isSelected: props.isSelected && !props.isDisabled,
        size: props.size,
        isDisabled: props.isDisabled
    })
)

const labelStyles = computed(() =>
    cva('font-normal', {
        variants: {
            isDefault: {
                true: 'text-gray-l-800'
            },
            isSelected: {
                true: 'text-primary-info'
            },
            isDisabled: {
                true: 'text-gray-disabled'
            },
            size: {
                [SwitchSelectorSize.LARGE]: 'text-[15px] leading-[18px]',
                [SwitchSelectorSize.MEDIUM]: 'text-[13px] leading-4'
            }
        }
    })({
        isDefault: !props.isSelected && !props.isDisabled,
        isSelected: props.isSelected && !props.isDisabled,
        isDisabled: props.isDisabled,
        size: props.size
    })
)

const switchBackgroundStyles = computed(() =>
    cva('inline-flex rounded-full ease-in-out duration-200', {
        variants: {
            isToggled: {
                true: 'bg-branding-500',
                false: 'bg-gray-disabled'
            },
            isDisabled: {
                true: 'cursor-not-allowed opacity-60'
            },
            size: {
                [SwitchSelectorSize.LARGE]: 'min-w-10 h-5 p-[3px]',
                [SwitchSelectorSize.MEDIUM]: 'min-w-8 h-4 p-0.5'
            }
        }
    })({
        isToggled: value.value,
        isDisabled: props.isDisabled,
        size: props.size
    })
)

const switchKnobStyles = computed(() =>
    cva('relative rounded-full bg-white transform ease-in-out duration-200', {
        variants: {
            size: {
                [SwitchSelectorSize.LARGE]: 'size-3.5',
                [SwitchSelectorSize.MEDIUM]: 'size-3'
            },
            translate: {
                [SwitchSelectorSize.LARGE]: value.value && 'translate-x-5',
                [SwitchSelectorSize.MEDIUM]: value.value && 'translate-x-4'
            }
        }
    })({
        size: props.size,
        translate: props.size
    })
)

const toggleSwitch = () => {
    if (!props.isDisabled) {
        value.value = !value.value
    }
}
</script>

<template>
    <div :class="containerStyles" @click="toggleSwitch">
        <div
            v-if="props.size === SwitchSelectorSize.MEDIUM"
            class="flex items-center justify-between w-full gap-2"
        >
            <span :class="labelStyles">
                {{ props.label }}
            </span>
            <div class="flex items-center gap-2">
                <IconExclamationCircle
                    v-if="isError"
                    isSolid
                    class="size-4 text-icon-secondary-negative"
                />
                <button
                    :id="`switch-${label}`"
                    type="button"
                    :class="switchBackgroundStyles"
                    :disabled="isDisabled"
                    :aria-checked="value"
                >
                    <div :class="switchKnobStyles" />
                </button>
            </div>
        </div>

        <div v-else class="flex items-center w-full gap-2">
            <button
                :id="`switch-${label}`"
                type="button"
                :class="switchBackgroundStyles"
                :disabled="isDisabled"
                :aria-checked="value"
            >
                <div :class="switchKnobStyles" />
            </button>
            <span :class="labelStyles">
                {{ props.label }}
            </span>
            <IconExclamationCircle
                v-if="isError"
                isSolid
                class="size-4 text-icon-secondary-negative ml-auto"
            />
        </div>

        <span
            v-if="isError"
            class="text-[11px] font-normal leading-[13px] text-icon-secondary-negative py-0.5 pr-0.5"
            :class="size === SwitchSelectorSize.LARGE && 'pl-12'"
        >
            {{ props.error }}
        </span>
    </div>
</template>
